import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { MetaDivider } from './MetaDivider';

export interface LibraryCardMetaProps {
  /** Status lozenge — leads the line. */
  status: ReactNode;
  /** "v3" from `formatVersion`. */
  version?: string;
  /** Already-formatted date from `formatLastEdited` — rendered as "Edited <date>". */
  editedLabel?: string;
  /** Quiet trailing descriptors — e.g. "Default", "Draft in progress", host text. They wrap, never overflow. */
  extraMeta?: string[];
  testId?: string;
  /** Lets the card's open target reference the line as its accessible description. */
  id?: string;
}

const LABEL_SX = {
  fontSize: 'var(--font-label-small-size, 0.75rem)',
  minWidth: 0
} as const;

/** A single-line, ellipsised text run; the full text stays in the DOM and in `title`. */
const ELLIPSIS_SX = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 } as const;

/**
 * The card's bottom line, pinned to the card's foot (`marginTop: auto`) so it aligns across a row
 * whose titles wrap differently.
 *
 * - The core line is the status lozenge, then the version + "Edited <date>" pair, and it never
 *   wraps: the lozenge keeps its size and the pair truncates with an ellipsis when a narrow
 *   (264px) card cannot hold the date (full text in `title`; the text is never removed from the
 *   DOM, so assistive tech loses nothing).
 * - `extraMeta` entries ("Default", "Draft in progress", host `metaText`) are siblings of the core
 *   line in the wrapping row: they drop to the next line when they do not fit, and each is
 *   ellipsised only if it alone is wider than the card.
 */
export function LibraryCardMeta({ status, version, editedLabel, extraMeta = [], testId, id }: LibraryCardMetaProps) {
  const edited = editedLabel ? `Edited ${editedLabel}` : '';
  const pairTitle = [version, edited].filter(Boolean).join(' · ');
  const extras = extraMeta.filter(Boolean);

  return (
    <Box
      id={id}
      data-testid={testId}
      data-slot="library-card-meta"
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 'var(--size-4) var(--size-8)',
        mt: 'auto',
        pt: 'var(--size-8)',
        minWidth: 0
      }}
    >
      <Box
        data-slot="library-card-meta-line"
        sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 'var(--size-8)', maxWidth: '100%', minWidth: 0 }}
      >
        <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
          {status}
        </Box>
        {pairTitle ? (
          <Box
            component="span"
            title={pairTitle}
            data-slot="library-card-meta-pair"
            sx={{
              ...LABEL_SX,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--size-8)',
              maxWidth: '100%',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              color: 'var(--text-body)'
            }}
          >
            {version ? (
              <Box component="span" sx={{ fontWeight: 'var(--font-weight-semibold)', flexShrink: 0 }}>
                {version}
              </Box>
            ) : null}
            {version && edited ? <MetaDivider /> : null}
            {edited ? (
              <Box component="span" sx={ELLIPSIS_SX}>
                {edited}
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Box>
      {extras.map((entry, index) => (
        // Divider and label travel as one unit, so a wrap moves both together and the rule is
        // never stranded at the end of the previous line. The first extra only gets a divider when
        // the version/edited pair rendered before it (no rule straight after the status lozenge).
        <Box
          key={`${index}-${entry}`}
          component="span"
          data-slot="library-card-meta-extra-unit"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--size-8)', maxWidth: '100%', minWidth: 0 }}
        >
          {index > 0 || pairTitle ? <MetaDivider /> : null}
          <Box
            component="span"
            title={entry}
            data-slot="library-card-meta-extra"
            sx={{ ...LABEL_SX, ...ELLIPSIS_SX, color: 'var(--text-caption)' }}
          >
            {entry}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
